/**
 * SupplyLedgerTab.js
 * SOW 3 Phase 1 — D-10 (RD-48)
 * Read-only supply transactions ledger view for SYSTEM + OPERATIONS admins.
 */

import React, { useState, useEffect, useCallback } from "react";
import S from "./adminStyles";

const API = "/api/supply-transactions";

const TX_TYPES = [
  "receive_vendor", "receive_donation", "breakdown",
  "transfer_to_client", "transfer_to_backstock",
  "adjustment_client", "adjustment_backstock",
  "shrinkage", "shrinkage_backstock",
];

const TX_BADGE = {
  receive_vendor:         "#16a34a",
  receive_donation:       "#16a34a",
  breakdown:              "#3b82f6",
  transfer_to_client:     "#3b82f6",
  transfer_to_backstock:  "#3b82f6",
  adjustment_client:      "#f59e0b",
  adjustment_backstock:   "#f59e0b",
  shrinkage:              "#dc2626",
  shrinkage_backstock:    "#dc2626",
};

const TX_LABEL = {
  receive_vendor:         "Receive (Vendor)",
  receive_donation:       "Receive (Donation)",
  breakdown:              "Breakdown",
  transfer_to_client:     "→ Client Stock",
  transfer_to_backstock:  "→ Backstock",
  adjustment_client:      "Adjustment (Client)",
  adjustment_backstock:   "Adjustment (Backstock)",
  shrinkage:              "Shrinkage",
  shrinkage_backstock:    "Shrinkage (Backstock)",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function SignedUnits({ value }) {
  if (value === null || value === undefined) return <span style={{ color: "#94a3b8" }}>—</span>;
  const color = value > 0 ? "#16a34a" : value < 0 ? "#dc2626" : "#64748b";
  return <span style={{ fontWeight: "600", color }}>{value > 0 ? `+${value}` : value}</span>;
}

export default function SupplyLedgerTab({ user, auth }) {
  const [txns,       setTxns]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [hasMore,    setHasMore]    = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filters
  const [filterTxType,   setFilterTxType]   = useState("");
  const [filterItemId,   setFilterItemId]   = useState("");
  const [filterItemInput, setFilterItemInput] = useState("");

  const fetchTxns = useCallback(async (cursor = null, append = false) => {
    try {
      append ? setLoadingMore(true) : setLoading(true);
      setError(null);

      const token = await auth.currentUser?.getIdToken();
      const params = new URLSearchParams({ limit: "50" });
      if (filterTxType) params.set("txType",      filterTxType);
      if (filterItemId) params.set("clientItemId", filterItemId);
      if (cursor)       params.set("startAfter",   cursor);

      const res = await fetch(`${API}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setTxns(prev => append ? [...prev, ...data.transactions] : data.transactions);
      setHasMore(data.hasMore);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [auth, filterTxType, filterItemId]);

  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  const handleItemSearch = () => setFilterItemId(filterItemInput.trim());
  const handleClearFilters = () => {
    setFilterTxType("");
    setFilterItemId("");
    setFilterItemInput("");
  };

  return (
    <div>
      {/* Header */}
      <div style={S.flexRowBetween}>
        <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b", margin: 0 }}>
          📋 Supply Ledger
        </h2>
        <span style={{ fontSize: "12px", color: "#94a3b8" }}>
          {txns.length} transaction{txns.length !== 1 ? "s" : ""} loaded
        </span>
      </div>

      {/* Filters */}
      <div style={{ ...S.card, marginBottom: "12px" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "4px" }}>
              TYPE
            </label>
            <select
              style={{ ...S.select, minWidth: "200px" }}
              value={filterTxType}
              onChange={e => setFilterTxType(e.target.value)}
            >
              <option value="">All Types</option>
              {TX_TYPES.map(t => (
                <option key={t} value={t}>{TX_LABEL[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "4px" }}>
              ITEM ID
            </label>
            <div style={{ display: "flex", gap: "6px" }}>
              <input
                style={{ ...S.input, width: "220px" }}
                placeholder="e.g. kroger-black-beans-can"
                value={filterItemInput}
                onChange={e => setFilterItemInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleItemSearch()}
              />
              <button style={S.btnSecondary} onClick={handleItemSearch}>Search</button>
            </div>
          </div>
          {(filterTxType || filterItemId) && (
            <button style={S.btnSecondary} onClick={handleClearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <p style={{ color: "#94a3b8", fontSize: "13px", textAlign: "center", padding: "24px" }}>
            Loading transactions...
          </p>
        ) : error ? (
          <p style={{ color: "#dc2626", fontSize: "13px" }}>Error: {error}</p>
        ) : txns.length === 0 ? (
          <p style={{ color: "#94a3b8", fontSize: "13px", textAlign: "center", padding: "24px" }}>
            No transactions found.
          </p>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {["Date", "Type", "Item", "Client Units", "Backstock Units", "Supplier", "Recorded By"].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {txns.map(tx => (
                    <tr key={tx.txId}>
                      <td style={{ ...S.td, whiteSpace: "nowrap", fontSize: "12px" }}>
                        {formatDate(tx.createdAt)}
                      </td>
                      <td style={S.td}>
                        <span style={S.badge(TX_BADGE[tx.txType] || "#64748b")}>
                          {TX_LABEL[tx.txType] || tx.txType}
                        </span>
                      </td>
                      <td style={{ ...S.td, fontSize: "12px", color: "#475569" }}>
                        {tx.clientItemId || tx.backstockItemId || "—"}
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <SignedUnits value={tx.unitsClient} />
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <SignedUnits value={tx.unitsBackstock} />
                      </td>
                      <td style={{ ...S.td, fontSize: "12px" }}>
                        {tx.supplierId || "—"}
                      </td>
                      <td style={{ ...S.td, fontSize: "11px", color: "#94a3b8" }}>
                        {tx.recordedByEmail || tx.recordedBy || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div style={{ textAlign: "center", marginTop: "16px" }}>
                <button
                  style={S.btnSecondary}
                  onClick={() => fetchTxns(nextCursor, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
